import KemImage from "@ch/assets/images/kem.svg";
import Icon from "@ch/components/Icons/Icon";
import type { AppState } from "@ch/redux-store/root-reducer";
import { type FC, useEffect, useMemo, useRef } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import { useSelector } from "react-redux";
import { useAudioControls } from "../hooks/useAudioControls";
import type { KemCitationResource } from "../utils/types";
import KemCitationSources from "./KemCitationSources";

interface Props {
  id?: string;
  message: string;
  createdAt?: string;
  outputAudioUrl?: string;
  resources?: KemCitationResource[];
  onResolvedAudioUrl?: (id: string, url: string) => void;
  onImageLoad?: () => void;
  onVisible?: (id: string) => void;
}

const KemChatBubble: FC<Props> = ({
  id,
  message,
  createdAt,
  outputAudioUrl,
  resources,
  onResolvedAudioUrl,
  onImageLoad,
  onVisible,
}) => {
  const userId = useSelector((s: AppState) => s.user.currentUser?.id);
  const { isLoadingAudio, isPlaying, togglePlay } = useAudioControls({
    id,
    outputAudioUrl,
    userId,
    onResolvedAudioUrl,
  });

  // Intersection observer to mark message as visible
  useEffect(() => {
    if (!id || !onVisible) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            onVisible(id);
            observer.disconnect();
          }
        });
      },
      {
        threshold: 0.5, // Mark as seen when 50% of the message is visible
        rootMargin: "0px",
      },
    );

    // Observe the outer container div
    const element = document.getElementById(`kem-message-${id}`);
    if (element) {
      observer.observe(element);
    }

    return () => {
      observer.disconnect();
    };
  }, [id, onVisible]);

  // Use a ref for onImageLoad to prevent markdownComponents from being recreated
  // on every render, which would cause the img to remount and fire onLoad repeatedly
  const onImageLoadRef = useRef(onImageLoad);
  onImageLoadRef.current = onImageLoad;

  /* eslint-disable react/no-unstable-nested-components */
  const markdownComponents = useMemo<Components>(
    () => ({
      p: ({ children }) => <p className="mb-2 block last:mb-0">{children}</p>,
      strong: ({ children }) => (
        <strong className="font-bold text-black-700">{children}</strong>
      ),
      em: ({ children }) => <em className="italic">{children}</em>,
      code: ({ children }) => (
        <code className="rounded bg-gray-200 px-1 text-ink-900">
          {children}
        </code>
      ),
      h1: ({ children }) => (
        <h1 className="mb-1 block text-lg font-bold text-black-700 md:text-xl">
          {children}
        </h1>
      ),
      h2: ({ children }) => (
        <h2 className="mb-1 block text-base font-bold text-black-700 md:text-lg">
          {children}
        </h2>
      ),
      h3: ({ children }) => (
        <h3 className="mb-1 block text-base font-bold text-black-700">
          {children}
        </h3>
      ),
      ul: ({ children }) => (
        <ul className="ml-4 list-disc space-y-1">{children}</ul>
      ),
      ol: ({ children }) => (
        <ol className="ml-4 list-decimal space-y-1">{children}</ol>
      ),
      li: ({ children }) => <li>{children}</li>,
      a: ({ children, href }) => {
        let url = href;
        if (
          href?.includes("risk-assessment-survey") ||
          href?.includes("navihealth.ai")
        ) {
          const u = new URL(href, window?.location?.origin);
          u?.searchParams?.set("source", "web");
          url = u?.toString();
        }
        return (
          <a
            className="font-bold text-black-700 underline"
            href={url}
            rel="noopener noreferrer"
            target="_blank"
          >
            {children}
          </a>
        );
      },
      img: ({ src, alt }) => (
        <img
          alt={alt ?? ""}
          className="mt-2 max-w-full rounded"
          onLoad={() => onImageLoadRef.current?.()}
          src={src}
        />
      ),
    }),
    [],
  );
  /* eslint-enable react/no-unstable-nested-components */

  const formatTime = (iso?: string) => {
    if (!iso) return "";
    const s = new Date(iso).toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
    return s.replace(/\b(am|pm)\b/i, (m) => m.toUpperCase());
  };

  return (
    <div
      className="mb-8 flex items-start pr-[32px] md:pr-[72px]"
      id={`kem-message-${id}`}
    >
      <div className="mr-1.5 h-[22px] w-[22px] shrink-0 overflow-hidden rounded-full md:mr-3 md:h-[42px] md:w-[42px]">
        <img alt="kem" className="h-full w-full rounded-full" src={KemImage} />
      </div>

      <div className="min-w-0 flex-1">
        <div className="w-full rounded-xl bg-gray-100 px-3 py-2">
          <div className="flex flex-col gap-2 text-sm text-ink-900 md:text-lg">
            <ReactMarkdown components={markdownComponents}>
              {message}
            </ReactMarkdown>
            {id && resources && resources.length > 0 ? (
              <KemCitationSources resources={resources} />
            ) : null}
          </div>
        </div>
        <div className="mt-2 flex items-center justify-between gap-2">
          <p className="shrink pr-2 text-base text-ink-700">
            {formatTime(createdAt)}
          </p>
          <button
            aria-label="Play message audio"
            className="kem-tour-audio-toggle flex shrink-0 items-center gap-1 text-ink-700 disabled:opacity-50"
            disabled={isLoadingAudio || !id}
            onClick={() => void togglePlay()}
            type="button"
          >
            <Icon name={isPlaying ? "pause" : "play"} size={18} />
            <span className="text-sm">
              {isLoadingAudio ? "Loading..." : isPlaying ? "Pause" : "Play"}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default KemChatBubble;
